import Layout from "./Layout";

export default function PrivacyPolicy() {
  return (
    <Layout>
      <div className="bg-white text-black">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-10 py-12 lg:py-16">
          {/* Page Header */}
          <div className="mb-10">
            <h1 className="text-3xl sm:text-4xl font-serif font-semibold tracking-wide">
              Privacy Policy
            </h1>

            <div className="w-16 h-1 bg-hover_dark_primary mt-4 mb-6"></div>

            <p className="text-sm text-gray-600">
              <span className="font-semibold text-black">Effective Date:</span>{" "}
              August 31, 2026
            </p>
          </div>

          {/* Introduction */}
          <div className="space-y-5 text-[15px] sm:text-base leading-7 text-gray-700">
            <p>
              At <strong className="text-black">Legacy Decor</strong> (“Legacy
              Decor”, “we”, “us”, “our”), we respect your privacy and are
              committed to protecting the personal information you provide while
              using <strong className="text-black">www.legacydecor.in</strong>{" "}
              (the “Website”).
            </p>

            <p>
              This Privacy Policy explains what information we may collect, how
              we use it, how it may be shared with service providers, and the
              choices available to you.
            </p>

            <p>
              By using our Website or placing an order, you acknowledge the
              practices described in this Privacy Policy, subject to applicable
              law.
            </p>
          </div>

          {/* Privacy Sections */}
          <div className="mt-10 space-y-9 text-[15px] sm:text-base leading-7 text-gray-700">
            {/* 1 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                1. Information We May Collect
              </h2>

              <p>
                Depending on how you interact with our Website, we may collect
                information such as:
              </p>

              {/* A */}
              <h3 className="font-semibold text-black mt-5 mb-3">
                A. Contact and Order Information
              </h3>

              <ul className="list-disc pl-6 space-y-1">
                <li>Name</li>
                <li>Mobile number</li>
                <li>Email address</li>
                <li>Billing address</li>
                <li>Shipping address</li>
                <li>Order details</li>
                <li>Products purchased</li>
                <li>Transaction reference information</li>
                <li>Customer-service communications</li>
              </ul>

              {/* B */}
              <h3 className="font-semibold text-black mt-5 mb-3">
                B. Payment Information
              </h3>

              <p>
                Payments may be processed through third-party payment gateways.
              </p>

              <p className="mt-3">
                Legacy Decor does not ordinarily require or store complete
                payment-card information such as full card numbers or CVV
                details on its own systems.
              </p>

              <p className="mt-3">
                Payment service providers may collect and process payment
                information in accordance with their own policies and applicable
                security standards.
              </p>

              {/* C */}
              <h3 className="font-semibold text-black mt-5 mb-3">
                C. Technical Information
              </h3>

              <p>
                When you visit our Website, certain technical information may be
                collected automatically, such as:
              </p>

              <ul className="list-disc pl-6 mt-3 space-y-1">
                <li>IP address</li>
                <li>Browser type</li>
                <li>Device information</li>
                <li>Operating system</li>
                <li>Pages visited</li>
                <li>Website interactions</li>
                <li>Referring pages</li>
                <li>
                  Approximate location information derived from technical data
                </li>
                <li>Other technical or usage information</li>
              </ul>
            </section>

            {/* 2 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                2. How We Use Your Information
              </h2>

              <p>
                We may use personal information for legitimate business
                purposes, including:
              </p>

              <ul className="list-disc pl-6 mt-3 space-y-1">
                <li>Processing and fulfilling orders</li>
                <li>Delivering products</li>
                <li>Processing payments</li>
                <li>Providing customer support</li>
                <li>Communicating order updates</li>
                <li>Handling transportation-damage claims</li>
                <li>Processing eligible cancellations or refunds</li>
                <li>Improving our products and Website</li>
                <li>Preventing fraud and misuse</li>
                <li>Maintaining Website security</li>
                <li>Complying with legal and regulatory obligations</li>
                <li>Responding to customer enquiries</li>
                <li>
                  Sending promotional communications where permitted and, where
                  required, with appropriate consent
                </li>
              </ul>
            </section>

            {/* 3 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                3. Sharing of Information
              </h2>

              <p>
                We may share relevant personal information with trusted third
                parties where reasonably necessary to provide our products and
                services.
              </p>

              <p className="mt-3">These may include:</p>

              <ul className="list-disc pl-6 mt-3 space-y-1">
                <li>Payment gateways</li>
                <li>Banks and payment service providers</li>
                <li>Courier and logistics companies</li>
                <li>Website hosting and technology providers</li>
                <li>Customer-support platforms</li>
                <li>Communication service providers</li>
                <li>Analytics providers</li>
                <li>Marketing service providers, where applicable</li>
                <li>Professional advisers</li>
                <li>
                  Government authorities or law-enforcement agencies where
                  legally required
                </li>
              </ul>

              <p className="mt-4">
                We share only information reasonably necessary for the relevant
                purpose, subject to applicable law and contractual or technical
                safeguards where appropriate.
              </p>
            </section>

            {/* 4 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                4. Cookies and Similar Technologies
              </h2>

              <p>The Website may use cookies and similar technologies to:</p>

              <ul className="list-disc pl-6 mt-3 space-y-1">
                <li>Keep the Website functioning</li>
                <li>Remember preferences</li>
                <li>Understand Website usage</li>
                <li>Improve Website performance</li>
                <li>Measure marketing effectiveness</li>
                <li>
                  Provide relevant content or advertising where applicable
                </li>
              </ul>

              <p className="mt-4">
                You may be able to control cookies through your browser or
                device settings. Disabling certain cookies may affect Website
                functionality.
              </p>
            </section>

            {/* 5 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                5. Third-Party Websites and Services
              </h2>

              <p>
                Our Website may contain links to third-party websites, payment
                services, social-media platforms or other external services.
              </p>

              <p className="mt-3">
                Legacy Decor is not responsible for the privacy practices of
                third-party websites or services.
              </p>

              <p className="mt-3">
                Customers should review the privacy policies of such third
                parties before providing personal information.
              </p>
            </section>

            {/* 6 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                6. Data Security
              </h2>

              <p>
                We take reasonable technical and organisational measures to
                protect personal information against unauthorised access,
                misuse, alteration, disclosure or loss.
              </p>

              <p className="mt-3">
                However, no method of transmitting or storing information
                electronically can be guaranteed to be completely secure.
              </p>
            </section>

            {/* 7 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                7. Data Retention
              </h2>

              <p>
                We retain personal information only for as long as reasonably
                necessary for the purposes for which it was collected, including
                order fulfilment, customer service, accounting, taxation, legal,
                regulatory, dispute-resolution and legitimate business
                requirements.
              </p>

              <p className="mt-3">
                Where information is no longer required and there is no legal or
                legitimate reason to retain it, we may delete, anonymise or
                otherwise dispose of it in accordance with applicable law.
              </p>
            </section>

            {/* 8 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                8. Your Rights and Choices
              </h2>

              <p>
                Subject to applicable law, you may have rights and choices
                relating to your personal data, including rights relating to:
              </p>

              <ul className="list-disc pl-6 mt-3 space-y-1">
                <li>Access to information</li>
                <li>Correction of inaccurate information</li>
                <li>
                  Withdrawal of consent where processing is based on consent
                </li>
                <li>Deletion/erasure where applicable</li>
                <li>Opting out of promotional communications</li>
                <li>Raising a grievance regarding personal-data processing</li>
              </ul>

              <p className="mt-4">
                Requests may be submitted using the contact details provided
                below.
              </p>

              <p className="mt-3">
                Certain requests may be subject to verification and applicable
                legal limitations.
              </p>
            </section>

            {/* 9 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                9. Marketing Communications
              </h2>

              <p>
                Where permitted and where appropriate consent is obtained,
                Legacy Decor may send promotional communications relating to
                products, offers, new collections or other updates.
              </p>

              <p className="mt-3">
                You may opt out of promotional communications by using the
                unsubscribe mechanism provided in the communication or by
                contacting us.
              </p>

              <p className="mt-3">
                You may continue to receive essential transactional
                communications relating to orders, payments, deliveries, refunds
                or customer-service matters.
              </p>
            </section>

            {/* 10 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                10. Children's Privacy
              </h2>

              <p>
                Our Website is intended for general consumers and is not
                knowingly directed at children in circumstances where applicable
                law requires parental consent.
              </p>

              <p className="mt-3">
                If we become aware that personal data has been collected from a
                child in circumstances where such collection is not permitted,
                we will take reasonable steps to address the situation in
                accordance with applicable law.
              </p>
            </section>

            {/* 11 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                11. Data Breaches
              </h2>

              <p>
                In the event of a personal-data breach, Legacy Decor will take
                appropriate steps required under applicable law, including
                applicable notification and remedial requirements.
              </p>
            </section>

            {/* 12 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                12. Changes to this Privacy Policy
              </h2>

              <p>
                We may update this Privacy Policy from time to time to reflect
                changes in our business, technology, legal requirements or
                data-processing practices.
              </p>

              <p className="mt-3">
                The revised policy will be posted on the Website with an updated
                effective date.
              </p>
            </section>

            {/* 13 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                13. Grievance / Privacy Contact
              </h2>

              <p>
                For privacy-related questions, requests or grievances, please
                contact:
              </p>

              <div className="mt-4 space-y-3">
                <p>
                  <strong className="text-black">Legacy Decor</strong>
                </p>

                <p>
                  Legal Entity:{" "}
                  <strong className="text-gray-500">Legacy Decor</strong>
                </p>

                <p>
                  Email:{" "}
                  <a
                    href="mailto:legacydecorvintage@gmail.com"
                    className="hover:text-hover_dark_primary hover:underline transition"
                  >
                    legacydecorvintage@gmail.com
                  </a>
                </p>

                <p>
                  Phone:{" "}
                  <a
                    href="tel:+919825081632"
                    className="hover:text-hover_dark_primary hover:underline transition"
                  >
                    (+91) 9825081632
                  </a>
                </p>

                <p>
                  Address: Shop No. 6, Ratnakar Atelier, Jodhpur Gam Road,
                  Ramdev Nagar, Satellite, Ahmedabad, Gujarat 380015
                </p>

                <p>
                  <span className="text-black">Working Hours:</span>{" "}
                  <span className="text-gray-500">10:00 AM - 8:30 PM</span>
                </p>
              </div>
            </section>

            {/* 14 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                14. Governing Law
              </h2>

              <p>
                This Privacy Policy shall be governed by applicable laws of
                India.
              </p>

              <p className="mt-3">
                Any disputes relating to this Privacy Policy shall be handled in
                accordance with applicable Indian law and the applicable
                jurisdictional requirements.
              </p>
            </section>
          </div>
        </div>
      </div>
    </Layout>
  );
}
